/**
 * BookingDetail.js — Detalhe de reserva de voo
 * Emula a tela da Rextur: segmentos, passageiros, tarifas, pagamento.
 */
import React from 'react'
import { X, User, Clock, Plane } from 'lucide-react'
import { colors } from '../styles/tokens'
import { getAirline } from '../config/airlines'

const interStyle = { fontFamily: "'Inter', -apple-system, sans-serif" }

function AirlineLogo({ code, size = 28 }) {
  const airline = getAirline(code)
  if (airline?.logo) {
    return (
      <img
        src={airline.logo}
        alt={code}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: '4px' }}
        onError={e => { e.target.style.display = 'none' }}
      />
    )
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '4px', background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: colors.accent }}>
      {code}
    </div>
  )
}

function formatBRL(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
}

function formatDateTime(d) {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch { return d }
}

function formatDate(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
}

function StatusBadge({ status }) {
  const map = {
    pending:   { label: 'Pendente',   bg: '#fef3c7', color: '#92400e' },
    confirmed: { label: 'Confirmada', bg: '#d1fae5', color: '#065f46' },
    cancelled: { label: 'Cancelada',  bg: '#fee2e2', color: '#991b1b' },
    completed: { label: 'Concluída',  bg: '#e0e7ff', color: '#3730a3' },
  }
  const s = map[status] || { label: status || 'Pendente', bg: '#fef3c7', color: '#92400e' }
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '999px', fontSize: '11px',
      fontWeight: 700, background: s.bg, color: s.color, textTransform: 'uppercase'
    }}>{s.label}</span>
  )
}

function InfoRow({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontWeight: highlight ? 700 : 600, color: highlight ? colors.primary : '#0f172a' }}>{value || '—'}</div>
    </div>
  )
}

function SegmentRow({ seg }) {
  if (!seg) return null
  const dep = seg.departureDateTime || seg.departure?.dateTime
  const arr = seg.arrivalDateTime || seg.arrival?.dateTime
  const orig = seg.originCode || seg.origin?.code || seg.departure?.airportCode
  const dest = seg.destinationCode || seg.destination?.code || seg.arrival?.airportCode
  const origName = seg.origin?.name || orig
  const destName = seg.destination?.name || dest
  const dur = seg.durationMinutes
    ? `${Math.floor(seg.durationMinutes/60)}h${String(seg.durationMinutes%60).padStart(2,'0')}m`
    : '—'

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '10px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AirlineLogo code={seg.airline?.code} size={24} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>
            {seg.airline?.code} {seg.flightNumber}
          </span>
        </div>
      </td>
      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a' }}>
        <div style={{ fontWeight: 600 }}>{formatDateTime(dep)}</div>
        <div style={{ color: '#64748b', fontSize: '11px' }}>{origName}</div>
      </td>
      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a' }}>
        <div style={{ fontWeight: 600 }}>{formatDateTime(arr)}</div>
        <div style={{ color: '#64748b', fontSize: '11px' }}>{destName}</div>
      </td>
      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
        {orig} → {dest}
      </td>
      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
        {seg.aircraftCode || '—'}
      </td>
      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
        {dur}
      </td>
    </tr>
  )
}

export default function BookingDetail({ booking, onClose }) {
  if (!booking) return null

  const outbound     = typeof booking.outbound === 'string' ? JSON.parse(booking.outbound) : booking.outbound
  const returnFlight = typeof booking.return_flight === 'string' ? JSON.parse(booking.return_flight) : booking.return_flight
  const passengers   = typeof booking.passengers === 'string' ? JSON.parse(booking.passengers) : (booking.passengers || [])

  const outboundSegs = outbound?.outbound?.segments || outbound?.segments || []
  const returnSegs   = returnFlight?.return?.segments || returnFlight?.segments || []

  // Preço vem do outbound salvo no Supabase
  const price = outbound?.price || {}
  const base  = price.base || 0
  const taxes = price.taxes || 0
  const rav   = price.commission || 0
  const total = booking.total_amount || price.total || 0

  // Localizador principal = PNR da Rextur (external_locator)
  const pnrRextur = booking.external_locator
  const locMoblix = booking.locator

  const thStyle = { padding: '8px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white', borderRadius: '12px',
        width: '100%', maxWidth: '780px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        ...interStyle
      }}>

        {/* Header */}
        <div style={{
          background: colors.primary, padding: '14px 20px',
          borderRadius: '12px 12px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Plane size={16} color={colors.accent} />
            <div>
              <div style={{ color: 'white', fontSize: '13px', fontWeight: 700 }}>
                Reserva Aérea — {booking.origin} → {booking.destination}
              </div>
              {(booking.locator || booking.external_locator) && (
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                  {pnrRextur && <span>PNR: <strong style={{color:'white'}}>{pnrRextur}</strong></span>}
                  {locMoblix && <span style={{marginLeft:'8px'}}>Loc: {locMoblix}</span>}
                </div>
              )}
            </div>
            <StatusBadge status={booking.status} />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>

          {/* Informações gerais */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
              Informações Gerais
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              <InfoRow label="PNR Rextur" value={booking.external_locator} highlight />
              <InfoRow label="Localizador" value={booking.locator} />
              <InfoRow label="Nº Reserva" value={booking.booking_number} />
              <InfoRow label="Criação" value={booking.created_at ? formatDate(booking.created_at) : '—'} />
              <InfoRow label="Ida" value={formatDate(booking.departure_date)} />
              <InfoRow label="Volta" value={booking.return_date ? formatDate(booking.return_date) : '—'} />
              <InfoRow label="Pagamento" value={booking.payment_method?.toUpperCase() || '—'} />
              <InfoRow label="Total" value={formatBRL(total)} highlight />
            </div>
          </div>

          {/* Segmentos ida */}
          {outboundSegs.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
                Segmentos — Ida ({booking.origin} → {booking.destination})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Voo</th>
                    <th style={thStyle}>Saída</th>
                    <th style={thStyle}>Chegada</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Rota</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Equip.</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {outboundSegs.map((seg, i) => <SegmentRow key={i} seg={seg} />)}
                </tbody>
              </table>
            </div>
          )}

          {/* Segmentos volta */}
          {returnSegs.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
                Segmentos — Volta ({booking.destination} → {booking.origin})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Voo</th>
                    <th style={thStyle}>Saída</th>
                    <th style={thStyle}>Chegada</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Rota</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Equip.</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {returnSegs.map((seg, i) => <SegmentRow key={i} seg={seg} />)}
                </tbody>
              </table>
            </div>
          )}

          {/* Passageiros */}
          {passengers.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
                Passageiros
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Perfil</th>
                    <th style={thStyle}>Faixa</th>
                    <th style={thStyle}>Sobrenome</th>
                    <th style={thStyle}>Nome</th>
                    <th style={thStyle}>Gênero</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {passengers.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 8px' }}>
                        <User size={16} color={colors.accent} />
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                        {p.type === 'adult' ? 'ADT' : p.type === 'child' ? 'CHD' : 'INF'}
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>{p.last_name}</td>
                      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a' }}>{p.first_name}</td>
                      <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748b' }}>{p.gender === 'M' ? 'Masculino' : p.gender === 'F' ? 'Feminino' : '—'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a', background: '#d1fae5', padding: '2px 8px', borderRadius: '999px' }}>
                          Reservada
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tarifas */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
              Tarifas
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Sobrenome/Nome</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Tarifa</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Tax. Emb.</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>RAV</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {passengers.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 8px', fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                      {p.last_name}/{p.first_name}
                    </td>
                    <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a', textAlign: 'right' }}>{formatBRL(base)}</td>
                    <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a', textAlign: 'right' }}>{formatBRL(taxes)}</td>
                    <td style={{ padding: '10px 8px', fontSize: '12px', color: '#0f172a', textAlign: 'right' }}>{formatBRL(rav)}</td>
                    <td style={{ padding: '10px 8px', fontSize: '12px', fontWeight: 700, color: colors.primary, textAlign: 'right' }}>{formatBRL(total)}</td>
                  </tr>
                ))}
                <tr style={{ background: '#f8fafc' }}>
                  <td colSpan={4} style={{ padding: '10px 8px', fontSize: '12px', fontWeight: 700, color: '#0f172a', textAlign: 'right' }}>Total</td>
                  <td style={{ padding: '10px 8px', fontSize: '14px', fontWeight: 700, color: colors.primary, textAlign: 'right' }}>{formatBRL(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* PIX */}
          {booking.pix_code && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
                Pagamento — PIX
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a', marginBottom: '8px' }}>PIX — COPIA E COLA</div>
                <code style={{ fontSize: '10px', color: '#0f172a', wordBreak: 'break-all', display: 'block', lineHeight: 1.5 }}>
                  {booking.pix_code}
                </code>
                {booking.pix_expires_at && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px', fontSize: '11px', color: '#64748b' }}>
                    <Clock size={11} /> Expira em {formatDateTime(booking.pix_expires_at)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contato */}
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '12px' }}>
              Contato
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {booking.contact_email} · {booking.contact_phone}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
